import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const brandPolicies = [
  {
    brand: "EcoFlow",
    icon: "ri-flashlight-line",
    scope: "All DELTA & RIVER Series power stations and accessories",
    enforcement: "Strict — violations result in dealer agreement termination",
    priceMatch: "No unauthorized discounting permitted below MAP",
    notes:
      "EcoFlow maintains one of the most actively enforced MAP programs in the industry. Promotional pricing (holiday events, bundle deals) may be offered by EcoFlow directly and is not dealer-initiated.",
  },
  {
    brand: "Nature's Generator",
    icon: "ri-sun-foggy-line",
    scope: "All solar generator systems, expansion kits, and panels",
    enforcement: "Active monitoring — price crawlers scan daily",
    priceMatch: "MAP pricing firm across all authorized channels",
    notes:
      "Nature's Generator requires all authorized resellers to sign a MAP compliance addendum. Pricing displayed on product pages reflects MAP-compliant values.",
  },
  {
    brand: "Renogy",
    icon: "ri-sun-line",
    scope: "Solar panels, charge controllers, inverters, batteries",
    enforcement: "Enforced through reseller agreement",
    priceMatch: "MAP applies to advertised pricing — invoice discounts may vary",
    notes:
      "Renogy's MAP policy applies specifically to advertised and publicly listed prices. In-cart or checkout discounts outside of official Renogy promotions are not permitted.",
  },
];

const faqs = [
  {
    q: "What does MAP stand for?",
    a: 'MAP stands for Minimum Advertised Price. It is the lowest price at which an authorized dealer is permitted to publicly advertise a product. MAP is set by the manufacturer — not the retailer — and exists to protect brand integrity, ensure consistent customer experience, and prevent a "race to the bottom" that undermines product quality and dealer service.',
  },
  {
    q: "Does MAP mean I'm paying more than I should?",
    a: "No. MAP pricing reflects the true value of the product and the service that comes with it — authorized warranty coverage, legitimate brand support, and verified product authenticity. Prices below MAP are often associated with gray-market stock, counterfeit units, or dealers who are not authorized and cannot honor manufacturer warranties.",
  },
  {
    q: "Can Iron Haven Supply ever sell below MAP?",
    a: "No. As an authorized dealer, Iron Haven Supply is contractually obligated to comply with all manufacturer MAP policies. We do not undercut MAP. What we can offer is exceptional service, bundle value, and support that gray-market sellers cannot.",
  },
  {
    q: "What if I find the same product cheaper elsewhere?",
    a: "If you see a lower advertised price, consider whether that seller is an authorized dealer. Purchases from unauthorized sellers typically void the manufacturer warranty. The manufacturer will not honor warranty claims on products sold outside authorized channels, regardless of what the seller claims.",
  },
  {
    q: "Do MAP policies apply to bundle pricing?",
    a: "Bundles are handled differently. When a manufacturer authorizes a bundle promotion, the effective per-unit price within the bundle may fall below individual MAP. Iron Haven Supply only runs bundles that are explicitly authorized by the brand. Independently assembled bundles designed to circumvent MAP are prohibited under our agreements.",
  },
  {
    q: "How do I report a MAP violation I've seen?",
    a: "If you believe another seller is advertising an authorized product below MAP, you can report it directly to the manufacturer using the contact information on their official website. You can also notify our team at support@ironhavensupply.com and we will escalate the report to the brand's compliance team.",
  },
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function MAPPolicyPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <main className="font-sans bg-white min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Navbar />

      {/* ── Hero ── */}
      <div className="pt-20 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-10 pb-12">
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-300"></i>
            <span className="text-gray-700 font-medium">MAP Policy</span>
          </nav>

          <div className="flex items-start gap-4">
            <div className="w-11 h-11 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
              <i className="ri-price-tag-3-line text-2xl text-amber-500"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
                Minimum Advertised Price (MAP) Policy
              </h1>
              <p className="text-gray-500 mt-2 text-base max-w-2xl">
                Iron Haven Supply is a fully authorized dealer and complies with all manufacturer MAP policies.
                Here's what that means for you as a buyer — and why it protects your purchase.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: "ri-verified-badge-line", label: "Dealer Status", value: "Fully Authorized" },
              { icon: "ri-shield-check-line", label: "Compliance", value: "100% MAP Compliant" },
              { icon: "ri-award-line", label: "Warranty", value: "Always Valid" },
              { icon: "ri-store-2-line", label: "Brands Covered", value: "All We Carry" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                  <i className={`${stat.icon} text-lg text-amber-500`}></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                  <p className="text-sm font-bold text-gray-900 leading-tight">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-14 space-y-14">

        {/* What is MAP */}
        <section id="what-is-map">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500">
              <i className="ri-information-line text-lg"></i>
            </div>
            <h2 className="text-xl font-extrabold text-gray-900">What Is a MAP Policy?</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
              <p>
                A <strong className="text-gray-800">Minimum Advertised Price (MAP) policy</strong> is a manufacturer-set rule that determines the lowest price at which an authorized dealer may publicly advertise a product. MAP is not a retail price — dealers are free to charge what they choose at the point of sale — but the publicly advertised price must remain at or above the MAP floor.
              </p>
              <p>
                MAP policies exist to protect the entire ecosystem: the brand's reputation, the quality of dealer service, and the consumer's expectation that an authorized product will be supported with a valid warranty and legitimate customer service.
              </p>
              <p>
                <strong className="text-gray-800">Iron Haven Supply strictly adheres to all MAP policies</strong> for every brand we carry. We are contractually bound by our authorized dealer agreements and take compliance seriously.
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  icon: "ri-lock-password-line",
                  color: "bg-amber-50 border-amber-100 text-amber-600",
                  title: "MAP is set by the manufacturer",
                  body: "Iron Haven Supply does not set MAP. We receive it from the brand and comply with it — no exceptions.",
                },
                {
                  icon: "ri-shield-star-line",
                  color: "bg-emerald-50 border-emerald-100 text-emerald-600",
                  title: "MAP protects your warranty",
                  body: "Products purchased below MAP from unauthorized sellers typically void the manufacturer warranty.",
                },
                {
                  icon: "ri-eye-line",
                  color: "bg-slate-50 border-slate-100 text-slate-600",
                  title: "MAP is publicly enforceable",
                  body: "Brands monitor advertised prices across authorized channels and act on violations — including dealer termination.",
                },
              ].map((box) => (
                <div key={box.title} className={`flex items-start gap-3 border rounded-xl p-4 ${box.color}`}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white shrink-0">
                    <i className={`${box.icon} text-base`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm mb-0.5">{box.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{box.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why it protects you */}
        <section id="buyer-protection" className="bg-gray-900 rounded-2xl px-6 md:px-10 py-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-500/20 rounded-xl shrink-0">
              <i className="ri-user-heart-line text-xl text-amber-400"></i>
            </div>
            <h2 className="text-xl font-extrabold text-white">Why MAP Compliance Protects You</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: "ri-verified-badge-line",
                title: "Genuine Product Guarantee",
                body: "MAP-compliant pricing is a reliable signal that the product is authentic, new, and sold through an authorized channel — not gray-market or refurbished.",
              },
              {
                icon: "ri-shield-check-line",
                title: "Full Warranty Coverage",
                body: "Every product purchased from an authorized, MAP-compliant dealer carries the complete manufacturer warranty. Prices that look too good to be true usually come with a warranty that doesn't exist.",
              },
              {
                icon: "ri-customer-service-2-line",
                title: "Real Post-Sale Support",
                body: "Authorized dealers have direct relationships with manufacturer support teams. If something goes wrong, we can escalate on your behalf — something unauthorized sellers cannot do.",
              },
              {
                icon: "ri-price-tag-2-line",
                title: "Stable, Predictable Pricing",
                body: "MAP prevents a race to the bottom where dealers cut service, skip warranty processing, and sell substandard stock to compete on price alone.",
              },
              {
                icon: "ri-exchange-funds-line",
                title: "No Hidden Costs",
                body: "MAP-compliant dealers don't make up margin through surprise fees, restocking charges, or shipping tricks. The price you see is the real price.",
              },
              {
                icon: "ri-store-2-line",
                title: "Dealer Accountability",
                body: "Authorized status requires ongoing compliance — with MAP, warranty procedures, and brand standards. You have recourse when you buy through a verified dealer.",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500/10 rounded-lg shrink-0 mt-0.5">
                  <i className={`${item.icon} text-amber-400 text-base`}></i>
                </div>
                <div>
                  <p className="text-white font-bold text-sm mb-1">{item.title}</p>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Brand-by-brand */}
        <section id="brand-policies">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500">
              <i className="ri-award-line text-lg"></i>
            </div>
            <h2 className="text-xl font-extrabold text-gray-900">MAP Policies by Brand</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Iron Haven Supply carries the following authorized brands, each with active MAP agreements in place.
          </p>
          <div className="space-y-4">
            {brandPolicies.map((bp) => (
              <div key={bp.brand} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                    <i className={`${bp.icon} text-lg text-amber-600`}></i>
                  </div>
                  <div>
                    <p className="font-extrabold text-gray-900 text-sm">{bp.brand}</p>
                    <p className="text-xs text-gray-400">{bp.scope}</p>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Enforcement Level</p>
                      <p className="text-sm font-bold text-gray-900">{bp.enforcement}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Price Match Restrictions</p>
                      <p className="text-sm font-bold text-gray-900">{bp.priceMatch}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-information-line text-sm text-amber-500"></i>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">{bp.notes}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reporting violations */}
        <section id="reporting">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500">
              <i className="ri-alert-line text-lg"></i>
            </div>
            <h2 className="text-xl font-extrabold text-gray-900">Reporting a MAP Violation</h2>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            If you believe a seller is advertising an authorized product below the manufacturer&apos;s MAP, you can report it. Protecting MAP integrity benefits all buyers by keeping the ecosystem of authorized dealers healthy.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                step: "1",
                icon: "ri-search-eye-line",
                title: "Document the violation",
                body: "Screenshot the listing showing the seller name, product, and advertised price below MAP. Note the URL and date.",
              },
              {
                step: "2",
                icon: "ri-mail-send-line",
                title: "Notify the manufacturer",
                body: "Contact the brand's compliance team directly via their official website. Most manufacturers have a dedicated violations reporting form.",
              },
              {
                step: "3",
                icon: "ri-customer-service-2-line",
                title: "CC our team (optional)",
                body: "Send your report to support@ironhavensupply.com and we will escalate directly to our brand contacts.",
              },
            ].map((s) => (
              <div key={s.step} className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500 rounded-full text-white font-extrabold text-sm mb-3">
                  {s.step}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${s.icon} text-sm text-amber-500`}></i>
                  </div>
                  <p className="font-bold text-gray-900 text-sm">{s.title}</p>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="bg-gray-900 rounded-2xl px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-extrabold text-base">Questions about our MAP compliance?</p>
              <p className="text-gray-400 text-xs mt-1">Our team is happy to clarify pricing, dealer status, or report a potential violation.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <a
                href="tel:8666054975"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm text-amber-400"></i>
                </div>
                (866) 605-4975
              </a>
              <a
                href="mailto:support@ironhavensupply.com"
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-send-line text-sm"></i>
                </div>
                Email Our Team
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500">
              <i className="ri-question-answer-line text-lg"></i>
            </div>
            <h2 className="text-xl font-extrabold text-gray-900">MAP Policy FAQ</h2>
          </div>
          <div className="space-y-2">
            {faqs.map((item, i) => (
              <div
                key={i}
                className={`border rounded-xl overflow-hidden transition-all ${
                  openFAQ === i ? "border-amber-200 bg-amber-50/40" : "border-gray-200 bg-white"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className={`font-semibold text-sm pr-4 leading-snug ${openFAQ === i ? "text-amber-700" : "text-gray-900"}`}>
                    {item.q}
                  </span>
                  <div className={`w-6 h-6 flex items-center justify-center shrink-0 rounded-full transition-colors ${
                    openFAQ === i ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500"
                  }`}>
                    <i className={`text-sm ${openFAQ === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                  </div>
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Related links */}
        <div className="border-t border-gray-100 pt-8 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-gray-400">Related pages:</span>
          <Link to="/warranty" className="text-amber-600 hover:underline cursor-pointer font-medium">Warranty Info</Link>
          <span className="text-gray-300">·</span>
          <Link to="/faq" className="text-amber-600 hover:underline cursor-pointer font-medium">Full FAQ</Link>
          <span className="text-gray-300">·</span>
          <Link to="/about" className="text-amber-600 hover:underline cursor-pointer font-medium">About Us</Link>
          <span className="text-gray-300">·</span>
          <Link to="/contact" className="text-amber-600 hover:underline cursor-pointer font-medium">Contact Support</Link>
        </div>
      </div>

      <Footer />
    </main>
  );
}
